/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiInputBase, LuiDomHelpers } from '@here/lui-base'
import scopedCss from 'lui-menu.scss'
import template from 'lui-menu.html'

export const WINDOW_FRAME_OFFSET = 20
export const CONTAINER_OFFSET_VERTICAL = 6

export default class LuiMenu extends LuiInputBase {
  static get is() {
    return 'lui-menu'
  }

  static get attributeToggles() {
    return ['placeholder', 'stickysearch', 'pullright']
  }

  static get hasClickTriggers() {
    return true
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  init() {
    this.addEventListener('select', this._selectAction)
  }

  onConnected() {
    this.delay(() => {
      this._update()
      this._registerEvents()
      this._checkScrollActive()
    })
  }

  onDisconnected() {
    this._disconnectListeners()
    this._unregisterEvents()
  }

  placeholderChanged(oldVal, newVal) {
    this._setPlaceholder(newVal)
  }

  get filterable() {
    return this._filterable || (this._filterable = this.hasAttribute('filterable'))
  }

  get stickySearch() {
    return this.filterable && (this._stickySearch || (this._stickySearch = this.hasAttribute('stickysearch')))
  }

  get pullRight() {
    return this._pullRight || (this._pullRight = this.hasAttribute('pullright'))
  }

  get uiTextInput() {
    this._uiTextInput = this._uiTextInput || this.shadowRoot.querySelector('lui-textfield')
    return this._uiTextInput
  }

  _registerEvents() {
    if (this.disabled) {
      return
    }
    this._onMenuScrollBound = this._onMenuScrollBound || this._onMenuScroll.bind(this)
    this._onUiTextInputBound = this._onUiTextInputBound || this._onUiTextInputChange.bind(this)

    this.addEventListener('scroll', this._onMenuScrollBound)
    this.uiTextInput.addEventListener('input', this._onUiTextInputBound)
  }

  _onMenuScroll(event) {
    const { target } = event
    const scrollEndReached = target.scrollTop + target.offsetHeight === target.scrollHeight
    if (scrollEndReached) {
      this.dispatchEvent(new Event('lui-menu:datalistEndReached', { bubbles: true, composed: true }))
    }
  }

  _checkScrollActive() {
    const isScrollActive = this.clientHeight < this.scrollHeight
    if (this.open && !isScrollActive) {
      this.dispatchEvent(new Event('lui-menu:datalistEndReached', { bubbles: true, composed: true }))
    }
  }

  _setPlaceholder(newVal) {
    const placeholder = (newVal || newVal === '') ? newVal : null
    if (placeholder !== null) {
      this.uiTextInput.setAttribute('placeholder', placeholder)
    } else {
      this.uiTextInput.removeAttribute('placeholder')
    }
  }

  _unregisterEvents() {
    this.removeEventListener('select', this._selectAction)
    this.removeEventListener('scroll', this._onMenuScrollBound)
    this.uiTextInput.removeEventListener('input', this._onUiTextInputBound)
  }
  _update() {
    if (!this.filterable) {
      this.uiTextInput.style.display = 'none'
      return
    }
    if (this.stickySearch) {
      this.uiTextInput.classList.add('lui-textfield__sticky')
    }
    this.uiTextInput.style.display = 'block'

    const uiOptions = this.filterable ? this._getFilteredItems(this.uiTextInput.value) : [...this.items]

    this.items.forEach(item => {
      if (!uiOptions.includes(item)) {
        item.style.display = 'none'
      } else {
        if (this.uiTextInput.value) {
          item.setAttribute('highlight', this.uiTextInput.value)
        } else {
          item.removeAttribute('highlight')
        }
        item.style.display = 'block'
      }
    })
  }

  _searchTextFilter(searchText, item) {
    return item.textContent.toLowerCase().includes(searchText)
  }

  _getFilteredItems(searchText) {
    const normalizedSearchText = (searchText || '').trim().toLowerCase()
    if (!normalizedSearchText) {
      return [...this.items]
    }

    return this.items.filter(this._searchTextFilter.bind(this, normalizedSearchText))
  }

  _onUiTextInputChange() {
    this._update(this._getFilteredItems(this.uiTextInput.value))
  }

  _onKeyDown(event) {
    function _focusItem(item) {
      const scrollCache = LuiDomHelpers.getScrollPositions(item.field)
      item.field.focus()
      scrollCache.forEach(cacheEntry => {
        cacheEntry.node.scrollTo(cacheEntry.x, cacheEntry.y)
      })
    }

    event.preventDefault()
    event.stopPropagation()

    const items = this.items
    let currentElement = this.querySelector(':focus')
    if (currentElement && currentElement.tagName !== 'LUI-MENUITEM') {
      currentElement = currentElement.querySelector('lui-menuitem')
    }
    const currentIndex = items.indexOf(currentElement)
    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
        if (currentIndex < items.length - 1) {
          _focusItem(items[currentIndex + 1])
        }
        break
      case 'Up':
      case 'ArrowUp':
        if (currentIndex > 0) {
          _focusItem(items[currentIndex - 1])
        }
        break
    }
  }

  _mousedownAnywhereAction(e) {
    if (this._elementInsideofMenu(e.target)) {
      this._mousedownMenuAction(e)
    } else {
      if (this.open) this.open = false
    }
  }

  _elementInsideofMenu(element) {
    return element.closest('lui-menu') === this
  }

  _mousedownMenuAction(e) {
  }

  _selectAction(e) {
    this._unselectLastItem(e.target)

    const changeEvent = new Event('change')
    changeEvent.shadowTarget = e.target
    this.dispatchEvent(changeEvent)
  }

  _unselectLastItem(currentSelectedItem) {
    this.selectedItems.map((item) => {
      if (currentSelectedItem !== item) item.selected = false
    })
  }

  _closeOnEscapeKeyAction(event) {
    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      this.open = false
    }
  }

  _selectItemByIndex(index) {
    if (index < 0) return
    this.items[index].selected = true
  }

  _connectListeners() {
    this._mousedownAnywhereBound = this._mousedownAnywhereBound || ((e) => this._mousedownAnywhereAction(e))
    this._checkEscapeKeyBound = this._checkEscapeKeyBound || ((e) => this._closeOnEscapeKeyAction(e))
    this.delay(() => {
      if (!this.open) return
      document.documentElement.addEventListener('mousedown', this._mousedownAnywhereBound)
      document.documentElement.addEventListener('keyup', this._checkEscapeKeyBound)
    })
  }

  _disconnectListeners() {
    document.documentElement.removeEventListener('mousedown', this._mousedownAnywhereBound)
    document.documentElement.removeEventListener('keyup', this._checkEscapeKeyBound)
  }

  _getContainer() {
    return this.closest('.lui-menu-container') || this.parentNode
  }

  openChanged(oldValue, newValue) {
    if (newValue === '') {
      if (!this.container) return
      this.uiTextInput.value = ''
      this._update()

      const offsetNode = this.container.offsetParent

      if (offsetNode && offsetNode !== window &&
        window.getComputedStyle(offsetNode).position === 'static') {
        // Happens when the offsetParent is a <td>/<th>
        offsetNode.style.position = 'relative'
      }

      const containerRect = this.container.getBoundingClientRect()
      const menuRect = this.getBoundingClientRect()
      const containerPositioned = window.getComputedStyle(this.container).position !== 'static'

      this.style.bottom = 'auto'
      this.style.left = 'auto'
      this.style.right = this.pullRight ? 0 : 'auto'
      this.style.top = 'auto'

      let x = 0
      let y = 0

      if (!containerPositioned) {
        x += this.container.offsetLeft
        y += this.container.offsetTop
      }

      if (containerRect.left + menuRect.width > window.innerWidth - WINDOW_FRAME_OFFSET) {
        x += containerRect.width - menuRect.width
      }

      if (!this.pullRight) {
        this.style.left = x + 'px'
      }

      const styles = window.getComputedStyle(this)
      const containerOffsetVertical = parseInt(styles.getPropertyValue('--lui-menu-container-offset-vertical'), 10) || 0

      const showMenuAbove = containerRect.top + menuRect.height + containerRect.height >
        window.innerHeight - WINDOW_FRAME_OFFSET &&
        containerRect.top - menuRect.height > 0

      if (showMenuAbove) {
        y -= menuRect.height + containerOffsetVertical
        this.classList.add('lui-menu--open-above')
      } else {
        y += containerRect.height + containerOffsetVertical
      }
      this.style.top = y + 'px'

      this._checkScrollActive()
    }
  }

  get container() {
    this._container = this._container || this._getContainer()
    return this._container
  }

  get selectedItems() {
    const menuItems = this.querySelectorAll('lui-menuitem')
    return Array.from(menuItems).filter((item) => item.selected)
  }

  get items() {
    return Array.from(this.querySelectorAll('lui-menuitem'))
  }

  get selectedIndex() {
    return this.items.indexOf(this.selectedItems[0])
  }

  set selectedIndex(index) {
    if (index === this.selectedIndex) return
    if (index >= this.items.length) return
    this._selectItemByIndex(index)
  }

  get open() {
    return this.hasAttribute('open')
  }

  set open(newValue) {
    if (newValue) {
      let cacheScroll = LuiDomHelpers.getScrollPositions(this)

      this.setAttribute('open', '')
      this._connectListeners()
      this.dispatchEvent(new Event('open'))

      this.shadowRoot.querySelector('[tabindex]').focus()
      if (this.selectedIndex !== -1) {
        this.items[this.selectedIndex].focus()
      } else {
        this.shadowRoot.querySelector('[tabindex]').focus()
      }
      cacheScroll.forEach(cacheEntry => {
        cacheEntry.node.scrollTo(cacheEntry.x, cacheEntry.y)
      })
    } else {
      this.removeAttribute('open')
      this.classList.remove('lui-menu--open-above')
      this._disconnectListeners()
      this.dispatchEvent(new Event('close'))
    }
  }

  get offsetWidth() {
    this.setAttribute('open', '')
    const offsetWidth = super.offsetWidth
    this.removeAttribute('open')
    return offsetWidth
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-menu/lui-menu.js
// module id = 242
// module chunks = 0 1 3